"use client";

import { useEffect, useState, useRef, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";

interface SpotlightRect {
  top: number;
  left: number;
  width: number;
  height: number;
}

interface TourSpotlightProps {
  /** CSS selector for the target element */
  targetSelector: string;
  /** Current step index - used to detect step changes even with same selector */
  stepIndex?: number;
  /** Extra padding around the spotlight area */
  padding?: number;
  /** Whether the spotlight is visible */
  isVisible: boolean;
  /** Click handler for the overlay (to dismiss) */
  onOverlayClick?: () => void;
  /** Callback with target rect for tooltip positioning */
  onTargetRect?: (rect: SpotlightRect | null) => void;
}

/**
 * TourSpotlight Component
 * Creates an SVG-based spotlight overlay that highlights a target element
 * Uses a clip path to cut out the spotlight area from the dark overlay
 * Both spotlight and tooltip follow the element on scroll
 */
export function TourSpotlight({
  targetSelector,
  stepIndex,
  padding = 8,
  isVisible,
  onOverlayClick,
  onTargetRect,
}: TourSpotlightProps) {
  const [rect, setRect] = useState<SpotlightRect | null>(null);
  const [windowSize, setWindowSize] = useState({ width: 0, height: 0 });
  const observerRef = useRef<ResizeObserver | null>(null);
  const targetRef = useRef<Element | null>(null);
  // Track previous step index to detect step changes (handles same selector across steps)
  const prevStepIndexRef = useRef<number | null>(null);
  // Track if we've already sent the initial rect for this step
  const rectSentRef = useRef<boolean>(false);

  // Update window size on mount and resize
  useEffect(() => {
    const updateWindowSize = () => {
      setWindowSize({
        width: window.innerWidth,
        height: window.innerHeight, // Use viewport height instead of document height
      });
    };

    updateWindowSize();
    window.addEventListener("resize", updateWindowSize);

    return () => {
      window.removeEventListener("resize", updateWindowSize);
    };
  }, []);

  // Update spotlight rect (follows element on scroll) and notify tooltip
  const updateSpotlightRect = useCallback(() => {
    const element = document.querySelector(targetSelector);
    if (!element) {
      const fallbackRect = {
        top: window.innerHeight / 2 - 50,
        left: window.innerWidth / 2 - 150,
        width: 300,
        height: 100,
      };
      setRect(fallbackRect);
      onTargetRect?.(fallbackRect); // Notify tooltip of position change
      return;
    }

    targetRef.current = element;
    const boundingRect = element.getBoundingClientRect();

    const newRect = {
      top: boundingRect.top - padding,
      left: boundingRect.left - padding,
      width: boundingRect.width + padding * 2,
      height: boundingRect.height + padding * 2,
    };

    setRect(newRect);
    onTargetRect?.(newRect); // Notify tooltip of position change on scroll
  }, [targetSelector, padding, onTargetRect]);

  // Find and track the target element
  useEffect(() => {
    if (!isVisible || !targetSelector) {
      setRect(null);
      prevStepIndexRef.current = null;
      rectSentRef.current = false;
      return;
    }

    // Check if this is a new step (step index changed)
    // This handles cases where multiple steps use the same selector
    const isNewStep = prevStepIndexRef.current !== stepIndex;
    if (isNewStep) {
      prevStepIndexRef.current = stepIndex ?? null;
      rectSentRef.current = false; // Reset so we send rect for new step
    }

    const initializeRect = (retryCount = 0) => {
      const element = document.querySelector(targetSelector);
      if (!element) {
        const fallbackRect = {
          top: window.innerHeight / 2 - 50,
          left: window.innerWidth / 2 - 150,
          width: 300,
          height: 100,
        };
        setRect(fallbackRect);
        // Only send rect once per step
        if (!rectSentRef.current) {
          rectSentRef.current = true;
          onTargetRect?.(fallbackRect);
        }
        return;
      }

      targetRef.current = element;
      const boundingRect = element.getBoundingClientRect();

      // If element is hidden (0 dimensions), retry after a delay to allow onShow hooks to run
      // This handles cases where the target element is initially hidden (e.g., minimized dialer)
      if (boundingRect.width === 0 && boundingRect.height === 0 && retryCount < 5) {
        setTimeout(() => {
          initializeRect(retryCount + 1);
        }, 150); // Give onShow hook time to open/reveal the element
        return;
      }

      const newRect = {
        top: boundingRect.top - padding,
        left: boundingRect.left - padding,
        width: boundingRect.width + padding * 2,
        height: boundingRect.height + padding * 2,
      };

      setRect(newRect);
      // Only send rect once per step - this is the key fix!
      if (!rectSentRef.current) {
        rectSentRef.current = true;
        onTargetRect?.(newRect);
      }
    };

    // First scroll element into view, then initialize rect
    const element = document.querySelector(targetSelector);
    if (element) {
      const boundingRect = element.getBoundingClientRect();
      // Check if element is outside viewport
      if (
        boundingRect.top < 0 ||
        boundingRect.bottom > window.innerHeight ||
        boundingRect.left < 0 ||
        boundingRect.right > window.innerWidth
      ) {
        element.scrollIntoView({
          behavior: 'instant',
          block: 'center',
          inline: 'center',
        });

        // Wait for scroll to complete, then initialize rect
        setTimeout(() => {
          initializeRect();
        }, 100);
      } else {
        initializeRect();
      }
    } else {
      initializeRect();
    }

    // Set up ResizeObserver to track element size changes
    observerRef.current = new ResizeObserver(() => {
      updateSpotlightRect();
    });

    const targetElement = element || document.querySelector(targetSelector);
    if (targetElement) {
      observerRef.current.observe(targetElement);
    }

    // Listen for scroll and resize to update spotlight position
    window.addEventListener("scroll", updateSpotlightRect, { passive: true });
    window.addEventListener("resize", updateSpotlightRect);

    return () => {
      observerRef.current?.disconnect();
      window.removeEventListener("scroll", updateSpotlightRect);
      window.removeEventListener("resize", updateSpotlightRect);
    };
  }, [targetSelector, stepIndex, padding, isVisible, onTargetRect, updateSpotlightRect]);

  if (!isVisible || !rect) {
    return null;
  }

  const { width: viewportWidth, height: viewportHeight } = windowSize;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.3 }}
        className="fixed inset-0 z-[9998] pointer-events-none"
        style={{ height: viewportHeight }}
      >
        {/* SVG Overlay with cutout - visual only */}
        <svg
          className="absolute inset-0 w-full h-full"
          style={{ height: viewportHeight }}
        >
          <defs>
            {/* Mask that creates the spotlight cutout */}
            <mask id="spotlight-mask">
              {/* White background = visible overlay */}
              <rect x="0" y="0" width="100%" height="100%" fill="white" />
              {/* Black rounded rect = transparent cutout */}
              <rect
                x={rect.left}
                y={rect.top}
                width={rect.width}
                height={rect.height}
                rx="8"
                ry="8"
                fill="black"
              />
            </mask>
          </defs>

          {/* Dark overlay with mask applied - pointer-events-auto to block clicks */}
          <rect
            x="0"
            y="0"
            width={viewportWidth}
            height={viewportHeight}
            fill="rgba(0, 0, 0, 0.75)"
            mask="url(#spotlight-mask)"
            style={{ pointerEvents: 'auto' }}
          />

          {/* Spotlight border/glow effect */}
          <rect
            x={rect.left}
            y={rect.top}
            width={rect.width}
            height={rect.height}
            rx="8"
            ry="8"
            fill="none"
            stroke="rgba(59, 130, 246, 0.5)"
            strokeWidth="2"
          />
        </svg>
      </motion.div>
    </AnimatePresence>
  );
}

export default TourSpotlight;
